import React, { useContext, useMemo } from 'react';
import classNames from 'classnames';
import ReorderResizeTable from 'components/ReorderResizeTable';
import { SheetEnterpriseMember, EnterpriseMember } from 'models/Enterprises';
import { KeyHeader, KeyCell, BODY_CONTENT_GENERATE } from './components';
import { AddMembersPeopleTableKeys } from './components/data';
import {
  checkHasEveryRequiredColumn,
  checkIfColumnIsValid,
  getInvalidOptionalMembersCount,
  prepareValuesForApi,
} from './components/utils';
import BulkAploadActions from 'components/BulkAploadActions';
import useCallbackAsync from 'hooks/useCallbackAsync';
import { Loader } from 'ncoded-component-library';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { useTranslation } from 'react-i18next';
import api from 'api';
import BulkUploadValidationBanner from '../../BulkUploadValidationBanner';

import './BulkUploadMembersTable.styles.scss';

type BulkUploadMembersTableProps = {
  className?: string;
  columns: string[];
  enterpriseId: string;
  members: SheetEnterpriseMember[];
  areAllRequiredFieldsValid: boolean;
  invalidMembersCount: number;
  onUpdateColumn: (key: string, newValue: string) => void;
  updateMember: (member: Partial<SheetEnterpriseMember>) => void;
  clearEnterpriseMembers: () => void;
  onUpload: () => void;
  onEditMember: (member: SheetEnterpriseMember) => void;
};

const BulkUploadMembersTable: React.FC<BulkUploadMembersTableProps> = (
  props,
) => {
  const {
    className,
    columns,
    members,
    enterpriseId,
    areAllRequiredFieldsValid,
    invalidMembersCount,
    onUpdateColumn,
    updateMember,
    onUpload,
    clearEnterpriseMembers,
    onEditMember,
  } = props;
  const { popSuccess } = useContext(PopNotificationsContext);

  const { t } = useTranslation();

  const classes = classNames('bulk-upload-members-table', className);

  const count = useMemo(() => members?.length, [members?.length]);

  const canSubmitMembers = useMemo(
    () =>
      !!count &&
      areAllRequiredFieldsValid &&
      checkHasEveryRequiredColumn(columns),
    [columns, count, areAllRequiredFieldsValid],
  );

  const numberOfProcessableColumns = useMemo(
    () => columns.filter(checkIfColumnIsValid).length,
    [columns],
  );

  const numberOfUnProcessableColumns = useMemo(
    () => columns.length - numberOfProcessableColumns,
    [columns, numberOfProcessableColumns],
  );

  const invalidOptionalMembersCount = useMemo(
    () => getInvalidOptionalMembersCount(members),
    [members],
  );

  const renderedContent = useMemo(
    () =>
      columns.reduce(
        (acc, col) => ({
          ...acc,
          [col]: (member: EnterpriseMember) => {
            if (checkIfColumnIsValid(col))
              return BODY_CONTENT_GENERATE[col as AddMembersPeopleTableKeys]({
                member,
                column: col,
                updateMember,
              });

            return <KeyCell column={col} member={member} />;
          },
        }),
        {},
      ),
    [columns, updateMember],
  );

  const renderedHeader = useMemo(
    () =>
      columns.reduce(
        (acc, col, index) => ({
          ...acc,
          [col]: () => (
            <KeyHeader
              column={col}
              index={index}
              allColumns={columns}
              onUpdateColumn={onUpdateColumn}
            />
          ),
        }),
        {},
      ),
    [columns, onUpdateColumn],
  );

  const [handleBulkUpload, isLoading] = useCallbackAsync(async () => {
    await api.enterprises.bulkCreateMembers(
      enterpriseId,
      prepareValuesForApi(members),
    );

    clearEnterpriseMembers();
    onUpload();
    popSuccess(
      t('membersImportedSuccesfully', {
        count,
      }),
    );
  });

  return (
    <div className={classes}>
      {isLoading && <Loader className="fixed" />}

      <BulkUploadValidationBanner
        invalidMembersCount={invalidMembersCount}
        invalidOptionalMembersCount={invalidOptionalMembersCount}
      />

      <ReorderResizeTable
        columns={columns}
        isDragDisabled
        isResizeDisabled
        data={members}
        hasHorizontalScrollFade={false}
        tableContentTransformations={renderedContent}
        tableHeaderContentTransformations={renderedHeader}
        onRowClick={onEditMember}
      />

      <BulkAploadActions
        title={t('uploadMembers', { count })}
        isDisabled={!canSubmitMembers}
        numberOfProcessableColumns={numberOfProcessableColumns}
        numberOfUnProcessableColumns={numberOfUnProcessableColumns}
        onClear={clearEnterpriseMembers}
        handleBulkUpload={handleBulkUpload}
      />
    </div>
  );
};

export default BulkUploadMembersTable;
